/**
 * The parent organization's binding of the shared {@link TimelineSection}.
 *
 * The mirror of `ManufacturerTimelineSection` and `BrandTimelineSection`, and
 * as thin: which document to write and where the images live. Everything else —
 * the list, the event modal, the hero banner, the transactional writes — is
 * shared.
 *
 * `enrich` runs `enrichParentOrgHistory` (Phase 2.5), which turns on the shared
 * ✨ AI Enrich button. The prompt is the organization's OWN story — formations,
 * mergers, changes of control; individual acquisitions belong to the ownership
 * events section, which has its own prompt and its own review.
 */
import type { TimelineEvent, TimelineEventImage } from '@watchkeeper/shared-types';
import type { ParentOrganization } from '../../types';
import { TimelineSection, type TimelineOwner } from '../Timeline/TimelineSection';
import {
  enrichParentOrgHistoryWithAI,
  PARENT_ORGANIZATIONS_COLLECTION,
  updateParentOrganization,
  uploadParentOrganizationHistoryHeroImage,
  uploadParentOrganizationTimelineImage,
} from '../../services/firebase';

interface Props {
  org: ParentOrganization;
  /** Lets the host refresh its copy after a write. */
  onOrgChange?: (next: ParentOrganization) => void;
}

export function ParentOrgTimelineSection({ org, onOrgChange }: Props) {
  const owner: TimelineOwner = {
    kind: 'parentOrg',
    id: org.id,
    name: org.displayName || org.name || org.id,
    history: org.history,
    storagePrefix: `${PARENT_ORGANIZATIONS_COLLECTION}/${org.id}/timeline/`,
    persist: async (history: TimelineEvent[]) => {
      await updateParentOrganization(org.id, { history });
      onOrgChange?.({ ...org, history });
    },
    uploadImage: (file, fileName) => uploadParentOrganizationTimelineImage(org.id, file, fileName),
    enrich: (input) => enrichParentOrgHistoryWithAI({ parentOrgId: org.id, ...input }),
    historyHeroImage: org.historyHeroImage,
    heroStoragePrefix: `${PARENT_ORGANIZATIONS_COLLECTION}/${org.id}/history-hero/`,
    uploadHeroImage: (file, fileName) =>
      uploadParentOrganizationHistoryHeroImage(org.id, file, fileName),
    persistHeroImage: async (historyHeroImage: TimelineEventImage | null) => {
      // `null` clears — `updateParentOrganization` turns it into deleteField(),
      // where an undefined would be dropped and leave the old hero in place.
      await updateParentOrganization(org.id, { historyHeroImage });
      onOrgChange?.({ ...org, historyHeroImage: historyHeroImage ?? undefined });
    },
  };

  return <TimelineSection owner={owner} />;
}
